import { LogItem, LogTimeByDateResponse } from "@lhd/types";
import { LOG_TYPE } from "@lhd/features/TrackingTime/HomeTracking/contants";

const FULL_CYCLE_TYPES = [
  LOG_TYPE.CHECKIN,
  LOG_TYPE.BREAK_START,
  LOG_TYPE.BREAK_END,
  LOG_TYPE.CHECKOUT,
] as const;

const CYCLE_REQUIRED_TYPES = [
  LOG_TYPE.BREAK_START,
  LOG_TYPE.BREAK_END,
  LOG_TYPE.CHECKOUT,
] as const;

const toMs = (log: LogItem): number =>
  new Date(log.logged_at || log.created_at).getTime();

const toMinuteMs = (log: LogItem): number => {
  const ms = toMs(log);
  return Number.isFinite(ms) ? Math.floor(ms / 60_000) * 60_000 : ms;
};

const hasLoggedAt = (log: LogItem): boolean =>
  typeof log.logged_at === "string" && log.logged_at.trim().length > 0;

export const findUnusedIncompletePersistedLogtime = (
  logtimes: LogItem[] | undefined,
  logType: LogItem["log_type"],
  usedIds: Set<number>,
): LogItem | undefined => {
  const candidates = (logtimes ?? []).filter(
    (log) =>
      log.log_type === logType &&
      typeof log.id === "number" &&
      log.id > 0 &&
      !usedIds.has(log.id) &&
      !hasLoggedAt(log),
  );
  if (candidates.length === 0) return undefined;
  return [...candidates].sort((a, b) => toMs(a) - toMs(b))[0];
};

export const findPersistedLogtimeInCycle = (
  cycle: LogItem[],
  logType: LogItem["log_type"],
  usedIds: Set<number>,
): LogItem | undefined => {
  const candidates = cycle.filter(
    (log) =>
      log.log_type === logType &&
      typeof log.id === "number" &&
      log.id > 0 &&
      !usedIds.has(log.id),
  );
  if (candidates.length === 0) return undefined;
  const incomplete = candidates.filter((log) => !hasLoggedAt(log));
  const pool = incomplete.length > 0 ? incomplete : candidates;
  return [...pool].sort((a, b) => toMs(a) - toMs(b))[0];
};

const dedupeVirtualLogtypesInSegment = (items: LogItem[]): LogItem[] => {
  const seenVirtualTypes = new Set<string>();
  const result: LogItem[] = [];
  for (const log of items) {
    const isVirtual = typeof log.id === "number" && log.id < 0;
    if (isVirtual) {
      if (seenVirtualTypes.has(log.log_type)) continue;
      seenVirtualTypes.add(log.log_type);
    }
    result.push(log);
  }
  return result;
};

const lifecycleTypeIndex = (log: LogItem): number => {
  switch (log.log_type) {
    case LOG_TYPE.CHECKIN:
      return 0;
    case LOG_TYPE.BREAK_START:
      return 1;
    case LOG_TYPE.BREAK_END:
      return 2;
    case LOG_TYPE.CHECKOUT:
      return 3;
    default:
      return 99;
  }
};

const createVirtualLog = (
  data: LogTimeByDateResponse,
  logType: LogItem["log_type"],
  anchor: LogItem,
  id: number,
  offsetMs: number,
): LogItem => {
  const base = Number.isFinite(toMs(anchor)) ? toMs(anchor) : Date.now();
  const createdAt = new Date(base + offsetMs).toISOString();
  return {
    id,
    worker_id: data.worker_id,
    company_id: data.company_id,
    log_type: logType,
    logged_at: "",
    work_date: data.date,
    latitude: null,
    longitude: null,
    note: null,
    created_at: createdAt,
    updated_at: createdAt,
    attachments: [],
  };
};

const toCreatedAtMs = (log: LogItem): number => {
  const ms = new Date(log.created_at).getTime();
  return Number.isFinite(ms) ? ms : 0;
};

const sortChronological = (a: LogItem, b: LogItem): number => {
  const loggedDiff = toMs(a) - toMs(b);
  if (loggedDiff !== 0) return loggedDiff;

  const typeDiff = lifecycleTypeIndex(a) - lifecycleTypeIndex(b);
  if (typeDiff !== 0) return typeDiff;

  const createdDiff = toCreatedAtMs(a) - toCreatedAtMs(b);
  if (createdDiff !== 0) return createdDiff;

  const idA = typeof a.id === "number" ? a.id : 0;
  const idB = typeof b.id === "number" ? b.id : 0;
  return idA - idB;
};

const sameMinuteBucket = (a: LogItem, b: LogItem): boolean =>
  toMinuteMs(a) === toMinuteMs(b);

const isBreakType = (log: LogItem): boolean =>
  log.log_type === LOG_TYPE.BREAK_START || log.log_type === LOG_TYPE.BREAK_END;

const findLastIndexByType = (
  segment: LogItem[],
  logType: LogItem["log_type"],
): number => {
  for (let i = segment.length - 1; i >= 0; i--) {
    if (segment[i]!.log_type === logType) return i;
  }
  return -1;
};

const isOrphanBreakAfterCheckout = (
  log: LogItem,
  lastCheckout: LogItem,
  segment: LogItem[],
): boolean => {
  if (!isBreakType(log)) return false;

  const checkin = findCheckinInSegment(segment);
  if (
    checkin != null &&
    isLogWithinCycleTimeWindow(log, checkin, lastCheckout)
  ) {
    return false;
  }

  if (
    log.log_type === LOG_TYPE.BREAK_END &&
    sameMinuteBucket(log, lastCheckout)
  ) {
    return false;
  }

  return true;
};

const moveOrphanBreaksToNextCycle = (cycles: LogItem[][]): LogItem[][] => {
  const next = cycles.map((segment) => [...segment]);

  for (let i = 0; i < next.length - 1; i++) {
    const segment = next[i]!;
    if (!segmentHasCheckin(segment)) continue;

    const lastCheckoutIdx = findLastIndexByType(segment, LOG_TYPE.CHECKOUT);
    if (lastCheckoutIdx < 0) continue;

    const lastCheckout = segment[lastCheckoutIdx]!;
    const afterCheckout = segment.slice(lastCheckoutIdx + 1);
    const orphans = afterCheckout.filter((log) =>
      isOrphanBreakAfterCheckout(log, lastCheckout, segment),
    );
    if (orphans.length === 0) continue;

    const keep = afterCheckout.filter(
      (log) => !isOrphanBreakAfterCheckout(log, lastCheckout, segment),
    );
    next[i] = [...segment.slice(0, lastCheckoutIdx + 1), ...keep];
    next[i + 1] = [...orphans, ...next[i + 1]!];
  }

  return next.filter((segment) => segment.length > 0);
};

const segmentHasCheckin = (segment: Array<LogItem>): boolean =>
  segment.some((log) => log.log_type === LOG_TYPE.CHECKIN);

const segmentHasCheckout = (segment: Array<LogItem>): boolean =>
  segment.some((log) => log.log_type === LOG_TYPE.CHECKOUT);

const cycleIsComplete = (segment: Array<LogItem>): boolean =>
  segmentHasCheckin(segment) && segmentHasCheckout(segment);

const lastCheckoutInSegment = (segment: LogItem[]): LogItem | undefined => {
  const idx = findLastIndexByType(segment, LOG_TYPE.CHECKOUT);
  return idx >= 0 ? segment[idx] : undefined;
};

const logBelongsToCurrentCycleAfterCheckout = (
  log: LogItem,
  segment: LogItem[],
): boolean => {
  const lastCheckout = lastCheckoutInSegment(segment);
  if (lastCheckout == null) {
    return log.log_type !== LOG_TYPE.CHECKOUT;
  }
  return sameMinuteBucket(log, lastCheckout);
};

const isLogWithinCycleTimeWindow = (
  log: LogItem,
  checkin: LogItem,
  checkout: LogItem | undefined,
): boolean => {
  const logMs = toMs(log);
  const checkinMs = toMs(checkin);

  if (!Number.isFinite(logMs) || !Number.isFinite(checkinMs)) {
    return (
      sameMinuteBucket(log, checkin) ||
      (checkout != null && sameMinuteBucket(log, checkout))
    );
  }

  const checkoutMs =
    checkout != null && Number.isFinite(toMs(checkout))
      ? toMs(checkout)
      : Number.POSITIVE_INFINITY;

  if (logMs >= checkinMs && logMs <= checkoutMs) return true;

  if (
    checkout != null &&
    Number.isFinite(checkoutMs) &&
    isBreakType(log) &&
    logMs >= checkinMs &&
    sameMinuteBucket(log, checkout)
  ) {
    return true;
  }

  return false;
};

const logBelongsToCurrentWorkCycle = (
  log: LogItem,
  current: LogItem[],
): boolean => {
  if (!segmentHasCheckin(current)) return false;
  if (!cycleIsComplete(current)) return true;

  const checkin = findCheckinInSegment(current);
  if (checkin == null) return false;

  if (isBreakType(log)) {
    return isLogWithinCycleTimeWindow(
      log,
      checkin,
      findCheckoutInSegment(current),
    );
  }

  return logBelongsToCurrentCycleAfterCheckout(log, current);
};

const findCheckinInSegment = (segment: LogItem[]): LogItem | undefined =>
  segment.find((log) => log.log_type === LOG_TYPE.CHECKIN);

const findCheckoutInSegment = (segment: LogItem[]): LogItem | undefined =>
  segment.find((log) => log.log_type === LOG_TYPE.CHECKOUT);

type BreakPair = { breakStart?: LogItem; breakEnd?: LogItem };

const BREAK_SCORE = {
  BOTH_STRICT: 10_000,
  ONE_STRICT: 1_000,
  AUTOFILL_EQUALS_CHECKOUT: 5_000,
  MINUTE_OVERLAP_START: 800,
  WEAK_WINDOW: 10,
} as const;

const strictBreakInCycleWindow = (
  log: LogItem,
  checkin: LogItem,
  checkout: LogItem,
): boolean => {
  const logMs = toMs(log);
  const checkinMs = toMs(checkin);
  const checkoutMs = toMs(checkout);
  if (
    !Number.isFinite(logMs) ||
    !Number.isFinite(checkinMs) ||
    !Number.isFinite(checkoutMs)
  ) {
    return false;
  }
  return logMs >= checkinMs && logMs <= checkoutMs;
};

const isMinutePrecisionLoggedAt = (log: LogItem): boolean => {
  if (!hasLoggedAt(log)) return false;
  const ms = toMs(log);
  return Number.isFinite(ms) && ms % 60_000 === 0;
};

const minuteOverlapsCycle = (
  log: LogItem,
  checkin: LogItem,
  checkout: LogItem,
): boolean => {
  const logMin = toMinuteMs(log);
  const startMin = toMinuteMs(checkin);
  const endMin = toMinuteMs(checkout);
  if (
    !Number.isFinite(logMin) ||
    !Number.isFinite(startMin) ||
    !Number.isFinite(endMin)
  ) {
    return false;
  }
  return logMin >= startMin && logMin <= endMin;
};

const hasCheckoutStrictlyBetween = (
  start: LogItem,
  end: LogItem,
  checkouts: LogItem[],
): boolean => {
  const startMs = toMs(start);
  const endMs = toMs(end);
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return false;
  return checkouts.some((co) => {
    const t = toMs(co);
    return Number.isFinite(t) && t > startMs && t < endMs;
  });
};

const pairBreaksInsideSegment = (breaks: LogItem[]): BreakPair[] => {
  const sorted = [...breaks].sort(sortChronological);
  const pairs: BreakPair[] = [];
  const startStack: LogItem[] = [];

  for (const log of sorted) {
    if (log.log_type === LOG_TYPE.BREAK_START) {
      startStack.push(log);
      continue;
    }
    if (log.log_type === LOG_TYPE.BREAK_END) {
      const start = startStack.pop();
      if (start != null) {
        pairs.push({ breakStart: start, breakEnd: log });
      } else {
        pairs.push({ breakEnd: log });
      }
    }
  }

  while (startStack.length > 0) {
    pairs.push({ breakStart: startStack.shift() });
  }

  return pairs;
};

const scoreBreakPairAgainstCycle = (
  pair: BreakPair,
  checkin: LogItem,
  checkout: LogItem,
): number => {
  let score = 0;

  const startIn =
    pair.breakStart != null &&
    strictBreakInCycleWindow(pair.breakStart, checkin, checkout);
  const endIn =
    pair.breakEnd != null &&
    strictBreakInCycleWindow(pair.breakEnd, checkin, checkout);

  if (startIn && endIn) {
    score += BREAK_SCORE.BOTH_STRICT;
  } else {
    if (startIn) score += BREAK_SCORE.ONE_STRICT;
    if (endIn) score += BREAK_SCORE.ONE_STRICT;
  }

  if (
    pair.breakEnd != null &&
    Number.isFinite(toMs(pair.breakEnd)) &&
    toMs(pair.breakEnd) === toMs(checkout)
  ) {
    score += BREAK_SCORE.AUTOFILL_EQUALS_CHECKOUT;
  }

  if (
    pair.breakStart != null &&
    !startIn &&
    isMinutePrecisionLoggedAt(pair.breakStart) &&
    minuteOverlapsCycle(pair.breakStart, checkin, checkout)
  ) {
    score += BREAK_SCORE.MINUTE_OVERLAP_START;
  } else if (
    pair.breakStart != null &&
    isLogWithinCycleTimeWindow(pair.breakStart, checkin, checkout)
  ) {
    score += BREAK_SCORE.WEAK_WINDOW;
  }

  if (
    pair.breakEnd != null &&
    !endIn &&
    isLogWithinCycleTimeWindow(pair.breakEnd, checkin, checkout)
  ) {
    score += BREAK_SCORE.WEAK_WINDOW;
  }

  const logMs = toMs(checkin);
  if (
    Number.isFinite(logMs) &&
    pair.breakStart != null &&
    pair.breakEnd != null &&
    toMs(pair.breakStart) === logMs &&
    toMs(pair.breakEnd) === logMs
  ) {
    const checkinId = typeof checkin.id === "number" ? checkin.id : 0;
    const checkoutId = typeof checkout.id === "number" ? checkout.id : 0;
    const startId =
      typeof pair.breakStart.id === "number" ? pair.breakStart.id : 0;
    const endId = typeof pair.breakEnd.id === "number" ? pair.breakEnd.id : 0;

    if (startId > checkinId && startId < checkoutId) {
      score += BREAK_SCORE.ONE_STRICT;
    }
    if (endId > checkinId && endId < checkoutId) {
      score += BREAK_SCORE.ONE_STRICT;
    }
  }

  return score;
};

const findBestCycleIndexForBreakPair = (
  pair: BreakPair,
  cycles: LogItem[][],
): { idx: number; rawScore: number } => {
  const viable = cycles
    .map((segment, idx) => ({ segment, idx }))
    .filter(
      ({ segment }) =>
        segmentHasCheckin(segment) && segmentHasCheckout(segment),
    );

  if (viable.length === 0) {
    return {
      idx: cycles.findIndex((segment) => segmentHasCheckin(segment)),
      rawScore: 0,
    };
  }

  const scored = viable.map(({ segment, idx }) => {
    const checkin = findCheckinInSegment(segment)!;
    const checkout = findCheckoutInSegment(segment)!;
    const rawScore = scoreBreakPairAgainstCycle(pair, checkin, checkout);
    return { idx, rawScore, score: rawScore - idx * 1e-6 };
  });

  const best = scored.reduce((a, b) => (b.score > a.score ? b : a));
  return { idx: best.idx, rawScore: best.rawScore };
};

const findFallbackCycleIndexForBreakPair = (
  pair: BreakPair,
  cycles: LogItem[][],
): number => {
  const anchor = pair.breakEnd ?? pair.breakStart;
  if (anchor == null) return -1;
  const anchorMs = toMs(anchor);
  if (!Number.isFinite(anchorMs)) return -1;

  const nextIdx = cycles.findIndex((segment) => {
    const checkin = findCheckinInSegment(segment);
    return checkin != null && toMs(checkin) >= anchorMs;
  });
  if (nextIdx >= 0) return nextIdx;

  for (let i = 0; i < cycles.length - 1; i++) {
    const checkout = findCheckoutInSegment(cycles[i]!);
    if (checkout != null && toMs(checkout) < anchorMs) {
      return i + 1;
    }
  }

  return cycles.length > 0 ? cycles.length - 1 : -1;
};

const canJoinOrphanBreakAcrossCheckout = (
  start: LogItem,
  end: LogItem,
  cycles: LogItem[][],
): boolean => {
  return cycles.some((segment) => {
    if (!segmentHasCheckin(segment) || !segmentHasCheckout(segment)) {
      return false;
    }
    const checkin = findCheckinInSegment(segment)!;
    const checkout = findCheckoutInSegment(segment)!;
    return (
      toMs(end) === toMs(checkout) &&
      (strictBreakInCycleWindow(start, checkin, checkout) ||
        (isMinutePrecisionLoggedAt(start) &&
          minuteOverlapsCycle(start, checkin, checkout)))
    );
  });
};

const joinOrphanBreakPairs = (
  pairs: BreakPair[],
  checkouts: LogItem[],
  cycles: LogItem[][],
): BreakPair[] => {
  const complete: BreakPair[] = [];
  const orphanStarts: LogItem[] = [];
  const orphanEnds: LogItem[] = [];

  for (const pair of pairs) {
    if (pair.breakStart != null && pair.breakEnd != null) {
      complete.push(pair);
    } else if (pair.breakStart != null) {
      orphanStarts.push(pair.breakStart);
    } else if (pair.breakEnd != null) {
      orphanEnds.push(pair.breakEnd);
    }
  }

  const usedStarts = new Set<LogItem>();
  const usedEnds = new Set<LogItem>();

  for (const end of orphanEnds) {
    let bestStart: LogItem | undefined;
    let bestScore = -1;

    for (const start of orphanStarts) {
      if (usedStarts.has(start)) continue;

      const crosses = hasCheckoutStrictlyBetween(start, end, checkouts);
      if (crosses && !canJoinOrphanBreakAcrossCheckout(start, end, cycles)) {
        continue;
      }

      const score = Math.max(
        0,
        ...cycles
          .filter(
            (segment) =>
              segmentHasCheckin(segment) && segmentHasCheckout(segment),
          )
          .map((segment) =>
            scoreBreakPairAgainstCycle(
              { breakStart: start, breakEnd: end },
              findCheckinInSegment(segment)!,
              findCheckoutInSegment(segment)!,
            ),
          ),
      );

      if (score > bestScore) {
        bestScore = score;
        bestStart = start;
      }
    }

    if (bestStart != null && bestScore > 0) {
      complete.push({ breakStart: bestStart, breakEnd: end });
      usedStarts.add(bestStart);
      usedEnds.add(end);
    }
  }

  for (const start of orphanStarts) {
    if (!usedStarts.has(start)) complete.push({ breakStart: start });
  }
  for (const end of orphanEnds) {
    if (!usedEnds.has(end)) complete.push({ breakEnd: end });
  }

  return complete;
};

const pairCompletenessScore = (pair: BreakPair): number => {
  if (pair.breakStart != null && pair.breakEnd != null) return 2;
  if (pair.breakStart != null || pair.breakEnd != null) return 1;
  return 0;
};

const reassignBreaksToBestCycle = (cycles: LogItem[][]): LogItem[][] => {
  const pairs: BreakPair[] = [];
  const stripped = cycles
    .map((segment) => {
      const breaks: LogItem[] = [];
      const nonBreaks: LogItem[] = [];
      for (const log of segment) {
        if (isBreakType(log)) {
          breaks.push(log);
        } else {
          nonBreaks.push(log);
        }
      }
      pairs.push(...pairBreaksInsideSegment(breaks));
      return nonBreaks;
    })
    .filter((segment) => segment.length > 0);

  if (pairs.length === 0) return cycles;

  const checkouts = stripped
    .flat()
    .filter((log) => log.log_type === LOG_TYPE.CHECKOUT);
  const resolvedPairs = joinOrphanBreakPairs(pairs, checkouts, stripped);
  const orderedPairs = [...resolvedPairs].sort(
    (a, b) => pairCompletenessScore(b) - pairCompletenessScore(a),
  );
  const result = stripped.map((segment) => [...segment]);

  for (const pair of orderedPairs) {
    const { idx: scoredIdx, rawScore } = findBestCycleIndexForBreakPair(
      pair,
      result,
    );
    let idx = scoredIdx;
    if (idx < 0 || rawScore <= 0) {
      idx = findFallbackCycleIndexForBreakPair(pair, result);
    }
    if (idx < 0) continue;
    if (pair.breakStart != null) result[idx]!.push(pair.breakStart);
    if (pair.breakEnd != null) result[idx]!.push(pair.breakEnd);
  }

  return result
    .map((segment) => [...segment].sort(sortChronological))
    .filter((segment) => segment.length > 0);
};

const shouldAppendToCurrentCycle = (
  log: LogItem,
  current: LogItem[],
): boolean => {
  if (!segmentHasCheckin(current)) return false;
  if (!cycleIsComplete(current)) return true;
  return logBelongsToCurrentWorkCycle(log, current);
};

const splitIntoWorkCycles = (
  sortedAsc: Array<LogItem>,
): Array<Array<LogItem>> => {
  const cycles: LogItem[][] = [];
  let current: LogItem[] = [];
  let pendingForNextCycle: LogItem[] = [];

  const flush = (): void => {
    if (current.length > 0) {
      cycles.push(current);
      current = [];
    }
  };

  for (const log of sortedAsc) {
    if (log.log_type === LOG_TYPE.CHECKIN) {
      if (
        current.length > 0 &&
        !segmentHasCheckin(current) &&
        current.every((s) => s.log_type === LOG_TYPE.CHECKOUT) &&
        sameMinuteBucket(current[0]!, log)
      ) {
        current.push(log);
        continue;
      }

      flush();
      current = [...pendingForNextCycle, log];
      pendingForNextCycle = [];
      continue;
    }

    if (shouldAppendToCurrentCycle(log, current)) {
      current.push(log);
    } else {
      pendingForNextCycle.push(log);
    }
  }

  flush();

  if (pendingForNextCycle.length > 0) {
    cycles.push(pendingForNextCycle);
  }

  return moveOrphanBreaksToNextCycle(reassignBreaksToBestCycle(cycles));
};

const sortByLifecycleThenChronological = (a: LogItem, b: LogItem): number => {
  const la = lifecycleTypeIndex(a);
  const lb = lifecycleTypeIndex(b);
  if (la !== lb) return la - lb;
  return sortChronological(a, b);
};

const insertVirtualAtLifecycleSlot = (
  forward: LogItem[],
  virtual: LogItem,
): void => {
  const target = lifecycleTypeIndex(virtual);
  const at = forward.findIndex((log) => lifecycleTypeIndex(log) > target);
  forward.splice(at >= 0 ? at : forward.length, 0, virtual);
};

const orderSegmentForDisplay = (items: LogItem[]): LogItem[] => {
  const withTime = items.filter(hasLoggedAt);
  const withoutTime = items.filter((log) => !hasLoggedAt(log));

  const forward = [...withTime].sort(sortChronological);

  const virtualBreakStart = withoutTime.filter(
    (log) => log.log_type === LOG_TYPE.BREAK_START,
  );
  const virtualBreakEnd = withoutTime.filter(
    (log) => log.log_type === LOG_TYPE.BREAK_END,
  );
  const otherVirtual = withoutTime.filter(
    (log) =>
      log.log_type !== LOG_TYPE.BREAK_START &&
      log.log_type !== LOG_TYPE.BREAK_END,
  );

  virtualBreakStart.forEach((log) =>
    insertVirtualAtLifecycleSlot(forward, log),
  );
  virtualBreakEnd.forEach((log) => insertVirtualAtLifecycleSlot(forward, log));
  otherVirtual
    .sort(sortByLifecycleThenChronological)
    .forEach((log) => insertVirtualAtLifecycleSlot(forward, log));

  return forward.reverse();
};

export const groupDisplayTimelineRowsIntoValidationCycles = (
  rows: LogItem[],
): LogItem[][] => {
  const cycles: LogItem[][] = [];
  let current: LogItem[] = [];

  for (const row of rows) {
    current.push(row);
    if (row.log_type === LOG_TYPE.CHECKIN) {
      cycles.push([...current].reverse());
      current = [];
    }
  }

  if (current.length > 0) {
    cycles.push([...current].reverse());
  }

  return cycles;
};

export const buildTrackingTimelineRows = (
  data: LogTimeByDateResponse,
): LogItem[] => {
  const source = [...(data.logtimes ?? [])].filter(
    (log) => log.log_type !== LOG_TYPE.NOTE,
  );

  if (source.length === 0) {
    const anchor: LogItem = {
      id: -99,
      worker_id: data.worker_id,
      company_id: data.company_id,
      log_type: LOG_TYPE.CHECKIN,
      logged_at: "",
      work_date: data.date,
      latitude: null,
      longitude: null,
      note: null,
      created_at: data.date,
      updated_at: data.date,
      attachments: [],
    };
    return orderSegmentForDisplay(
      FULL_CYCLE_TYPES.map((logType, idx) =>
        createVirtualLog(data, logType, anchor, -(idx + 1), idx + 1),
      ),
    );
  }

  const sourceWithIndex = source.map((log, idx) => ({ log, idx }));
  const sorted = sourceWithIndex.sort((a, b) => {
    const loggedDiff = toMs(a.log) - toMs(b.log);
    if (loggedDiff !== 0) return loggedDiff;
    return a.idx - b.idx;
  });
  const asc = sorted.map(({ log }) => log);

  const segmentBlocks: LogItem[][] = [];
  let virtualId = -1;
  let offsetMs = 1;
  const usedPersistedIds = new Set<number>();

  const assignPersistedOrVirtual = (
    data: LogTimeByDateResponse,
    requiredType: LogItem["log_type"],
    anchor: LogItem,
    segment: LogItem[],
  ): LogItem => {
    const persistedInSegment = segment.find(
      (log) =>
        log.log_type === requiredType &&
        typeof log.id === "number" &&
        log.id > 0,
    );
    if (persistedInSegment) {
      usedPersistedIds.add(persistedInSegment.id);
      return persistedInSegment;
    }
    const existing = findUnusedIncompletePersistedLogtime(
      source,
      requiredType,
      usedPersistedIds,
    );
    if (existing) {
      usedPersistedIds.add(existing.id);
      return existing;
    }
    const row = createVirtualLog(
      data,
      requiredType,
      anchor,
      virtualId,
      offsetMs,
    );
    virtualId -= 1;
    offsetMs += 1;
    return row;
  };

  const rawSegments = splitIntoWorkCycles(asc);

  for (const segment of rawSegments) {
    if (segment.length === 0) continue;

    const anchor =
      segment.find((log) => log.log_type === LOG_TYPE.CHECKIN) ?? segment[0]!;
    const hasCheckin = segmentHasCheckin(segment);
    const segmentTypes = new Set(segment.map((log) => log.log_type));
    const extras: LogItem[] = [];

    segment.forEach((log) => {
      if (typeof log.id === "number" && log.id > 0) {
        usedPersistedIds.add(log.id);
      }
    });

    const requiredTypes = hasCheckin ? CYCLE_REQUIRED_TYPES : FULL_CYCLE_TYPES;
    requiredTypes.forEach((requiredType) => {
      if (segmentTypes.has(requiredType)) return;
      extras.push(
        assignPersistedOrVirtual(data, requiredType, anchor, segment),
      );
    });

    segmentBlocks.push(
      orderSegmentForDisplay(
        dedupeVirtualLogtypesInSegment([...segment, ...extras]),
      ),
    );
  }

  return segmentBlocks.reverse().flat();
};
